import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { ImageUploadField } from '../../components/forms/ImageUploadField.jsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.jsx';

const FONT_LABELS = { system: 'System (default)', serif: 'Serif', rounded: 'Rounded', mono: 'Mono' };
const FALLBACK_THEME_PRESETS = [
  {
    id: 'classic-bistro',
    name: 'Classic Bistro',
    description: 'Ivory menu-card styling with bottle-green ink and warm marigold details.',
    theme: { primaryColor: '#22352a', secondaryColor: '#d5891d', backgroundColor: '#fbf9f0', textColor: '#22352a', font: 'system' },
  },
];
const COLOR_LABELS = {
  primaryColor: 'Primary',
  secondaryColor: 'Accent',
  backgroundColor: 'Background',
  textColor: 'Text',
};

export function MenuAppearancePage() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { data: schemaMeta } = useApiQuery(() => api.get('/api/appearance/schema'), []);
  const { data: appearance, loading, error } = useApiQuery(() => api.get(`/api/menus/${menuId}/appearance`), [menuId]);
  const { data: media } = useApiQuery(() => api.get('/api/media'), []);

  const [config, setConfig] = useState({
    branding: { logoUrl: null, showLogo: true, showName: true, showDescription: true, coverImageUrl: null },
    theme: { preset: 'classic-bistro' },
    layout: { categoryNavigation: 'tabs', itemCardLayout: 'list', imageShape: 'rounded', density: 'comfortable' },
    itemDisplay: { showImages: true, showPrice: true, showDescription: true, showAttributes: true },
  });
  const [saveError, setSaveError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const savedConfigRef = useRef(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    if (appearance?.config) {
      setConfig(appearance.config);
      savedConfigRef.current = JSON.stringify(appearance.config);
      setDirty(false);
    }
  }, [appearance]);

  useEffect(() => {
    function warnAboutUnsavedChanges(event) {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', warnAboutUnsavedChanges);
    return () => window.removeEventListener('beforeunload', warnAboutUnsavedChanges);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    function interceptNavigation(event) {
      if (!(event.target instanceof window.Element)) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin) return;
      if (destination.pathname === window.location.pathname && destination.search === window.location.search) return;
      event.preventDefault();
      setPendingNavigation(`${destination.pathname}${destination.search}${destination.hash}`);
    }
    document.addEventListener('click', interceptNavigation, true);
    return () => document.removeEventListener('click', interceptNavigation, true);
  }, [dirty]);

  function applyConfig(updater) {
    const next = typeof updater === 'function' ? updater(config) : updater;
    setConfig(next);
    setDirty(JSON.stringify(next) !== savedConfigRef.current);
    setSaveMessage(null);
    setSaveError(null);
  }

  function update(section, field, value) {
    applyConfig((c) => ({ ...c, [section]: { ...c[section], [field]: value } }));
  }

  function mediaFor(id) {
    if (!id || !media) return null;
    const found = media.items.find((m) => m.id === id);
    return found ? { id: found.id, url: found.url } : null;
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaveError(null);
    setSaveMessage(null);
    setSaving(true);
    try {
      await api.put(`/api/menus/${menuId}/appearance`, config);
      savedConfigRef.current = JSON.stringify(config);
      setDirty(false);
      setSaveMessage('Appearance saved. Publish the menu when you are ready for customers to see it.');
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (error) return <div className="alert alert-error">{error.message}</div>;
  if (loading || !config) return <span className="spinner" aria-label="Loading" />;

  const fontOptions = schemaMeta?.options.fontOptions ?? ['system'];
  const themePresets = schemaMeta?.options.themePresets ?? FALLBACK_THEME_PRESETS;
  const activePreset = config.theme.preset ?? themePresets[0]?.id ?? 'classic-bistro';

  function applyThemePreset(preset) {
    applyConfig((c) => ({
      ...c,
      theme: {
        ...c.theme,
        ...preset.theme,
        preset: preset.id,
      },
    }));
  }

  return (
    <div className="page appearance-page">
      <div className="page-header">
        <div>
          <h1>Appearance</h1>
          <Link className="back-link" to={`/menus/${menuId}`}>← Back to menu</Link>
        </div>
      </div>
      <p>Every menu already looks good by default — change only what you want to customize.</p>

      {saveError && <div className="alert alert-error">{saveError}</div>}
      {saveMessage && <div className="alert alert-success">{saveMessage}</div>}

      <form onSubmit={handleSave}>
        <div className="appearance-toolbar">
          <div>
            <strong>Menu appearance</strong>
            <span>{dirty ? 'Unsaved changes' : 'Changes apply to your next publish.'}</span>
          </div>
          <div className="appearance-toolbar__actions">
            <Link to={`/menus/${menuId}/preview`} className="btn btn-sm">Preview</Link>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !dirty} aria-busy={saving || undefined}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>

        <nav className="appearance-nav" aria-label="Appearance sections">
          <a href="#branding">Branding</a>
          <a href="#theme">Theme</a>
          <a href="#layout">Layout</a>
          <a href="#item-display">Item display</a>
        </nav>

        <div className="panel" id="branding" style={{ marginBottom: 16 }}>
          <h2>Branding</h2>
          <ImageUploadField
            label="Logo"
            kind="LOGO"
            value={mediaFor(config.branding.logoMediaId)}
            onChange={(m) => update('branding', 'logoMediaId', m?.id ?? null)}
            hint="Use a square logo. Keep text centered and avoid transparent edges where possible."
          />
          <ImageUploadField
            label="Cover image"
            kind="BANNER"
            value={mediaFor(config.branding.coverImageMediaId)}
            onChange={(m) => update('branding', 'coverImageMediaId', m?.id ?? null)}
            hint="Use a wide landscape photo, ideally 3:1 or 16:9. Keep important content near the center because edges crop on smaller screens."
          />
          <label className="checkbox" style={{ marginBottom: 8 }}>
            <input type="checkbox" checked={config.branding.showLogo} onChange={(e) => update('branding', 'showLogo', e.target.checked)} />
            Show logo
          </label>
          <label className="checkbox" style={{ marginBottom: 8 }}>
            <input type="checkbox" checked={config.branding.showName} onChange={(e) => update('branding', 'showName', e.target.checked)} />
            Show restaurant name
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={config.branding.showDescription}
              onChange={(e) => update('branding', 'showDescription', e.target.checked)}
            />
            Show description
          </label>
        </div>

        <div className="panel" id="theme" style={{ marginBottom: 16 }}>
          <h2>Theme</h2>
          <p className="field-hint" style={{ marginTop: -4 }}>
            Choose a polished starting point, then fine-tune colors if needed.
          </p>
          <div className="theme-presets">
            {themePresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyThemePreset(preset)}
                aria-pressed={activePreset === preset.id}
                style={{
                  border: activePreset === preset.id ? `2px solid ${preset.theme.primaryColor}` : '1px solid var(--color-border)',
                  borderRadius: 12,
                  background: preset.theme.backgroundColor,
                  color: preset.theme.textColor,
                  padding: 12,
                  textAlign: 'left',
                  cursor: 'pointer',
                  boxShadow: activePreset === preset.id ? '0 0 0 3px var(--color-primary-soft)' : 'var(--shadow-sm)',
                }}
              >
                <span style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {[preset.theme.primaryColor, preset.theme.secondaryColor, preset.theme.backgroundColor, preset.theme.textColor].map((color) => (
                    <span
                      key={color}
                      aria-hidden="true"
                      style={{ width: 18, height: 18, borderRadius: 999, background: color, border: '1px solid rgba(15,23,42,.16)' }}
                    />
                  ))}
                </span>
                <strong style={{ display: 'block', marginBottom: 4 }}>{preset.name}</strong>
                <span style={{ display: 'block', fontSize: '0.75rem', opacity: 0.78 }}>{preset.description}</span>
              </button>
            ))}
          </div>
          <h3>Fine tune</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            {['primaryColor', 'secondaryColor', 'backgroundColor', 'textColor'].map((field) => (
              <div className="field" key={field}>
                <label htmlFor={field}>{COLOR_LABELS[field]}</label>
                <input id={field} type="color" value={config.theme[field]} onChange={(e) => update('theme', field, e.target.value)} />
              </div>
            ))}
          </div>
          <div className="field">
            <label htmlFor="font">Font</label>
            <select id="font" value={config.theme.font} onChange={(e) => update('theme', 'font', e.target.value)}>
              {fontOptions.map((f) => (
                <option key={f} value={f}>
                  {FONT_LABELS[f] ?? f}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="panel" id="layout" style={{ marginBottom: 16 }}>
          <h2>Layout</h2>
          <div className="field">
            <label>Category navigation</label>
            <p className="field-hint">Horizontal scroll pills. Swipe to see more categories on smaller screens.</p>
          </div>
          <div className="field">
            <label htmlFor="itemLayout">Item layout</label>
            <select id="itemLayout" value={config.layout.itemCardLayout} onChange={(e) => update('layout', 'itemCardLayout', e.target.value)}>
              <option value="list">List</option>
              <option value="grid">Grid</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="imageShape">Image shape</label>
            <select id="imageShape" value={config.layout.imageShape} onChange={(e) => update('layout', 'imageShape', e.target.value)}>
              <option value="rounded">Rounded</option>
              <option value="square">Square</option>
              <option value="circle">Circle</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="density">Density</label>
            <select id="density" value={config.layout.density} onChange={(e) => update('layout', 'density', e.target.value)}>
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </div>
        </div>

        <div className="panel" id="item-display" style={{ marginBottom: 16 }}>
          <h2>What customers see on each item</h2>
          {[
            ['showImages', 'Images'],
            ['showPrice', 'Price'],
            ['showDescription', 'Description'],
            ['showAttributes', 'Attributes'],
          ].map(([field, label]) => (
            <label className="checkbox" style={{ marginBottom: 8 }} key={field}>
              <input type="checkbox" checked={config.itemDisplay[field]} onChange={(e) => update('itemDisplay', field, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>

        <div className="appearance-footer-action">
          <span className="field-hint">{dirty ? 'You have unsaved appearance changes.' : 'All appearance changes are saved.'}</span>
          <button type="submit" className="btn btn-primary" disabled={saving || !dirty} aria-busy={saving || undefined}>
            {saving ? 'Saving…' : 'Save appearance'}
          </button>
        </div>
      </form>
      {pendingNavigation && <ConfirmDialog
        title="Leave without saving?"
        description="Your appearance changes will be lost."
        confirmLabel="Discard changes"
        onConfirm={() => {
          const destination = pendingNavigation;
          setDirty(false);
          setPendingNavigation(null);
          navigate(destination);
        }}
        onCancel={() => setPendingNavigation(null)}
      />}
    </div>
  );
}
